import { Color, Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons';
import { isEmpty, startCase } from 'lodash/fp';
import {
  ComponentProps,
  FC,
  memo,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CategoricalChartState } from 'recharts/types/chart/types';
import { Payload } from 'recharts/types/component/DefaultLegendContent';
import { AxisInterval, BaseAxisProps, DataKey } from 'recharts/types/util/types';
import styled, { ThemeContext } from 'styled-components';

import { ChartLegendContent } from 'dashboard/components/BarAndLineChartWithLegend/ChartLayout';
import ChartLayout, { ChartTooltipHeader } from 'dashboard/layouts/ChartLayout';
import { formatNumber, formatNumberLarge } from 'dashboard/lib/utils/formatters';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { Heading, Label } from 'design-system/components/Text';
import { Elevation } from 'design-system/variables/effect';
import { FontWeight } from 'design-system/variables/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import Dot from 'images/dashboard/dot.svg';
import useToggle from 'lib/hooks/useToggle';

// These are important for the tooltip placement
const TOOLTIP_Y_OFFSET = 120;
const FILTERED_KEY_HEIGHT = 24;
const TOOLTIP_X_OFFSET = 16;

export interface RenderLabelProps {
  x: number;
  y: number;
  fill: string;
  value: unknown;
  index: number;
  width: number;
  height: number;
}

interface ActivatableProps {
  active?: boolean;
  hasActive?: boolean;
  hovered?: boolean;
}

export const ChartTooltipLabelContainer = styled.div<ActivatableProps>`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-gap: ${Spacing[2]};
  padding: ${Spacing[1]} ${Spacing[4]} ${Spacing[1]} ${Spacing[2]};
  width: 100%;
  min-width: 260px;

  .title {
    justify-self: start;
  }

  justify-self: end;

  transition-duration: ${TransitionDuration.Fast};
  transition: opacity ${TransitionDuration.Fast};
  opacity: ${({ active, hasActive }) => (hasActive ? (active ? 1 : 0.3) : 1)};
`;

const ChartTooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
  max-height: 300px;
  overflow-y: auto;
  overscroll-behavior: auto;

  box-shadow: ${Elevation.Mid};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  padding: ${Spacing[2]} ${Spacing[3]};
  z-index: ${Z_INDICES.toast};
  background-color: ${({ theme }) => theme.color.primary.white};
`;

const StaticTooltipContainer = styled.div<{
  x: number;
  y: number;
}>`
  position: absolute;
  z-index: ${Z_INDICES.tooltip};
  top: ${({ y }) => y}px;
  left: ${({ x }) => x}px;
  box-shadow: ${Elevation.Mid};

  transition:
    height ${TransitionDuration.Medium},
    width ${TransitionDuration.Medium};
`;

const HeaderContainer = styled.div`
  padding: ${Spacing[2]};
  padding-top: ${Spacing[1]};
  width: 100%;
  float: left;
  display: flex;
  flex-direction: column;
`;

const StyledChartTooltipLabel = styled(Label).attrs(() => ({ size: 'md', weight: 'bold' }))`
  color: ${({ theme }) => theme.color.text.primary};
  font-weight: ${FontWeight.Regular};
  justify-self: end;
`;

const StyledButton = styled(Button)`
  margin-left: auto;
  position: relative;
  background-color: white;
`;

const ChartHeader: FC<PropsWithChildren<object>> = ({ children }) => (
  <HeaderContainer>
    <Heading size='sm' style={{ display: 'flex' }}>
      {children}
    </Heading>
  </HeaderContainer>
);

export interface BarAndLineChartWithLegendV2Props {
  data: Record<any, any>[];
  dataKeys?: DataKey<any>[];
  colors: string[];
  stackColors?: string[];
  barKeys: string[];
  barsLabel?: string;
  lineKeys: string[];
  linesLabel?: string;
  lineGroups?: Record<any, any>;
  stackedBars?: boolean;
  // need chartWidth for testing - https://stackoverflow.com/questions/60215153/when-mounted-in-enzyme-why-does-a-recharts-chart-with-a-responsivecontainer-not
  chartWidth?: number;

  createTooltipHeader: (label: string | number) => string;
  legendFormatter?: (value: string) => string;
  onDeepLink?: (datum: Record<any, any>, keys: string[]) => void;
  renderTooltip?: (hoveredKey: string) => ReactNode;
  legendHeight?: number | 'auto';
  keyFormatter?: (value: string) => string;
  hideLegend?: boolean;
  renderBarLabel?: (props: RenderLabelProps, idx: number) => ReactNode;
  chartHeight?: number;
  numbersAxisFormatter?: (value: number) => string;

  // x-axis options
  xAxisHeight?: number;
  xAxisLabel?: string;
  xAxisTextAnchor?: string;
  xAxisAngle?: number;
  xDataKey: string | ((data: unknown) => string);
  xFormatter?: BaseAxisProps['tickFormatter'];
  xAxisTick?: BaseAxisProps['tick'];
  interval?: AxisInterval;

  // y-axis options
  yAxisTicks?: (number | string)[];
  percentageAxisDomain?: [number | string, number | string];

  initialFilteredLegendKeys?: string[];
  showZeros?: boolean;
  scrollEnabled?: boolean;
  legendIcons?: Record<string, ReactElement>;
  legendNumberOpts?: Intl.NumberFormatOptions;
  allowEscapeViewBoxX?: boolean;
}

const LINE_MARGIN = 8;

const DIMENSIONS = {
  height: 290,
  margin: {
    top: LINE_MARGIN * 4,
    left: -LINE_MARGIN * 2,
  },
};

export const BarAndLineChartWithLegendV2 = ({
  data,
  barKeys,
  barsLabel = 'Volume',
  lineKeys,
  linesLabel = 'Rate',
  lineGroups,
  stackedBars = false,
  colors,
  stackColors,
  chartWidth,
  createTooltipHeader,
  legendFormatter = startCase,
  onDeepLink,
  renderTooltip,
  legendHeight,
  keyFormatter,
  hideLegend, // x-axis
  xFormatter,
  xAxisLabel,
  xDataKey,
  xAxisHeight = 30,
  xAxisTextAnchor = 'middle', // passing undefined textAnchor to XAxis causes the behavior to change from the default ('middle') to 'start'
  xAxisAngle,
  xAxisTick,
  renderBarLabel,
  // y-axis
  yAxisTicks,
  chartHeight,
  initialFilteredLegendKeys,
  showZeros,
  scrollEnabled = true,
  legendIcons = {},
  legendNumberOpts = {},
  percentageAxisDomain,
  allowEscapeViewBoxX = true,
  numbersAxisFormatter,
  interval = 0,
}: BarAndLineChartWithLegendV2Props) => {
  const theme = useContext(ThemeContext);

  // Container References
  const tooltipRef = useRef<HTMLDivElement>();
  const containerRef = useRef<HTMLDivElement>(null);
  const staticContainerRef = useRef<HTMLDivElement>();

  // State variables for static
  const [mouseHovered, { enable: activeTooltip, disable: deactivateTooltip }] = useToggle(false);
  const [isStatic, { enable: activateStaticToolTip, disable: deactivateStaticToolTip }] =
    useToggle(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipPayload, setTooltipPayload] = useState(null);
  const [tooltipLabel, setTooltipLabel] = useState(null);
  const [yScrollPosition, setYScrollPosition] = useState(0);

  const [filteredLegendKeys, setFilteredLegendKeys] = useState(
    initialFilteredLegendKeys == null ? [] : [...initialFilteredLegendKeys],
  );
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [legendHovered, { enable: setLegendHovered, disable: clearLegendHovered }] = useToggle();

  // When there is re-rendering, we'll want to scroll to exactly where the Y position of the scroll was
  useLayoutEffect(() => {
    staticContainerRef.current?.scrollTo(0, yScrollPosition);
  }, [yScrollPosition, hoveredKey]);

  // Deactivate the tooltip and set everything back
  const deactivateTooltipAndReset = () => {
    deactivateStaticToolTip();
    setHoveredKey(null);
  };

  const toggleStaticToolTip = (event: CategoricalChartState) => {
    // If it's already enabled, disable it
    if (isStatic) {
      deactivateTooltipAndReset();
      return;
    }

    if (event == null) {
      return;
    }

    // Ensure that we've clicked at a point where we have a live Tooltip visible
    if (event.activePayload != null && tooltipRef.current != null) {
      activateStaticToolTip();
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setTooltipPayload(event.activePayload);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setTooltipLabel(event.activeLabel);
      // We move the tooltip to a set portion away from the current cursor. This is so that we
      // ensure the user understands that the sticky tooltip has been employed
      const toolTipPositions = tooltipRef.current.getBoundingClientRect();
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const containerPositions = containerRef.current.current.getBoundingClientRect();

      // Gotta adjust for some padding
      setTooltipPosition({
        x: toolTipPositions.x - containerPositions.x + TOOLTIP_X_OFFSET,
        // TODO: [jonny] Revisit this
        // Adjust the height of the tooltip and then account for the top padding
        y:
          toolTipPositions.y -
          containerPositions.y -
          FILTERED_KEY_HEIGHT * filteredLegendKeys.length +
          TOOLTIP_Y_OFFSET,
      });
    }
  };

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const StaticToolTip: FC<{
    active?: boolean;
    label?: number;
    payload?: any[];
    hoveredKey: string | null;
    createHeader: (label: number) => string;
    legendFormatter: (value: string) => string;
    keyToUnit: (value: string) => string;
    tooltipRef: RefObject<HTMLDivElement>;
    showZeros: boolean;
  }> = ({ active, payload, label, hoveredKey, legendFormatter, createHeader, keyToUnit }) => {
    if (!active || payload == null) {
      return null;
    }

    const countLabels = payload
      .slice()
      .map(({ color, dataKey, value }) =>
        value > 0 || (showZeros && value === 0) ? (
          <>
            <ChartTooltipLabelContainer
              key={dataKey}
              active={true}
              hovered={dataKey === hoveredKey}
              hasActive={hoveredKey != null}
              onMouseEnter={() => {
                setHoveredKey(dataKey);
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                setYScrollPosition(staticContainerRef.current?.scrollTop);
              }}
            >
              <StyledChartTooltipLabel className='title'>
                <Dot style={{ color }} /> {legendFormatter(String(dataKey))}
              </StyledChartTooltipLabel>
              <StyledChartTooltipLabel>
                {formatNumber(+value)}
                {keyToUnit(dataKey as string)}
              </StyledChartTooltipLabel>
            </ChartTooltipLabelContainer>
          </>
        ) : null,
      )
      .filter(Boolean);

    return (
      countLabels.length > 0 && (
        <StaticTooltipContainer
          onMouseLeave={() => setHoveredKey(null)}
          y={tooltipPosition.y}
          x={tooltipPosition.x}
        >
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          <ChartTooltipContainer ref={staticContainerRef}>
            <ChartHeader>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              {createHeader(label)}{' '}
              <StyledButton
                size='sm'
                variant='basic'
                width='icon'
                onClick={() => {
                  deactivateTooltipAndReset();
                }}
              >
                <XIcon />
              </StyledButton>
            </ChartHeader>
            {countLabels}
          </ChartTooltipContainer>
        </StaticTooltipContainer>
      )
    );
  };

  const handleLegendClick = useCallback(
    ({ dataKey }: Payload) =>
      typeof dataKey === 'string' &&
      setFilteredLegendKeys(
        filteredLegendKeys.includes(dataKey)
          ? filteredLegendKeys.filter((s) => s !== dataKey)
          : filteredLegendKeys.concat(dataKey),
      ),
    [filteredLegendKeys, data, initialFilteredLegendKeys],
  );

  // handle a reload of the initialFilteredLegendKeys
  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setFilteredLegendKeys(initialFilteredLegendKeys);
  }, [initialFilteredLegendKeys]);

  const handleLegendMouseEnter = useCallback(({ dataKey }: Payload) => {
    setLegendHovered();
    typeof dataKey === 'string' && setHoveredKey(dataKey);
  }, []);

  const handleLegendMouseLeave = useCallback(() => {
    clearLegendHovered();
    setHoveredKey(null);
  }, []);

  const handleDeepLink = useCallback(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (_: unknown, index: number) => onDeepLink(data[index], filteredLegendKeys),
    [onDeepLink, data, filteredLegendKeys],
  );

  const tooltipContent = renderTooltip ? (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    () => renderTooltip(hoveredKey)
  ) : (
    <TooltipContent
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      tooltipRef={tooltipRef}
      createHeader={createTooltipHeader}
      hoveredKey={hoveredKey}
      legendFormatter={legendFormatter}
      keyToUnit={(key) => (lineKeys.includes(key) ? '%' : '')}
      showZeros={showZeros}
      legendNumberOpts={legendNumberOpts}
    />
  );

  const effectiveChartHeight = chartHeight ?? DIMENSIONS.height;
  const totalHeight = effectiveChartHeight + xAxisHeight;

  // When we legendHeight is undefined the ChartContentLegend is automatically sized
  const effectiveLegendHeight =
    legendHeight === 'auto' ? undefined : (legendHeight ?? effectiveChartHeight);
  return (
    <ChartLayout
      data-test='bar-and-line-chart'
      onMouseOver={activeTooltip}
      onMouseOut={deactivateTooltip}
    >
      {isStatic && (
        <StaticToolTip
          createHeader={createTooltipHeader}
          hoveredKey={hoveredKey}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          payload={tooltipPayload}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          label={tooltipLabel}
          legendFormatter={legendFormatter}
          keyToUnit={(key) => (lineKeys.includes(key) ? '%' : '')}
          active={true}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          tooltipRef={tooltipRef}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          showZeros={showZeros}
        />
      )}
      <ResponsiveContainer height={totalHeight} width={chartWidth || '100%'} ref={containerRef}>
        <ComposedChart
          data={data}
          margin={DIMENSIONS.margin}
          onClick={toggleStaticToolTip}
          style={{ cursor: 'pointer' }}
        >
          <CartesianGrid vertical={false} stroke={theme.color.border.secondary} />
          <XAxis
            dataKey={xDataKey}
            // NOTE: [jonny] The default setting for the x-axis is to be categorical. Frankly,
            // this is not the best representation of the date values, seeing as they are clearly a continuous
            // spectrum of unix strings. Unfortunately, the default domain is [0, 'auto']. This means that it'll go
            // from a unix date of 0 to the number of data points. To avoid a highly disruptive change, we'll leave
            // this as a category and revisit the domain + axis type combination
            type='category'
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            tickFormatter={xFormatter}
            tick={
              xAxisTick ?? {
                fontSize: theme.text.paragraph.mdBold.fontSize,
                fill: theme.color.text.tertiary,
              }
            }
            interval={interval}
            angle={xAxisAngle}
            textAnchor={xAxisTextAnchor}
            height={xAxisLabel ? xAxisHeight + LINE_MARGIN * 3 : xAxisHeight}
            label={
              xAxisLabel && {
                value: xAxisLabel,
                position: 'insideBottom',
                fill: Color.CoreSlate1000,
                fontSize: theme.text.paragraph.lgBold.fontSize,
                fontWeight: theme.text.paragraph.lgBold.fontWeight,
                offset: LINE_MARGIN,
              }
            }
          />
          <YAxis
            yAxisId={1}
            orientation='left'
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            ticks={yAxisTicks}
            tickFormatter={numbersAxisFormatter ?? formatNumberLarge}
            tick={{
              fontSize: theme.text.paragraph.mdBold.fontSize,
              fill: theme.color.text.tertiary,
            }}
            allowDecimals={false}
          />
          <YAxis
            yAxisId={2}
            unit='%'
            orientation='right'
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            ticks={yAxisTicks}
            tickFormatter={(val) =>
              isEmpty(legendNumberOpts)
                ? formatNumberLarge(val)
                : formatNumber(val, legendNumberOpts)
            }
            tick={{
              fontSize: theme.text.paragraph.mdBold.fontSize,
              fill: theme.color.text.tertiary,
            }}
            allowDecimals={false}
            domain={percentageAxisDomain}
          />
          <Tooltip
            isAnimationActive={false}
            active={mouseHovered && !isStatic}
            content={tooltipContent}
            allowEscapeViewBox={{
              x: allowEscapeViewBoxX,
            }}
          />
          )
          {hideLegend === true ? null : (
            <Legend
              layout='vertical'
              verticalAlign='top'
              align='right'
              formatter={legendFormatter}
              iconType='circle'
              inactiveColor={Color.CoreSlate50}
              // eslint-disable-next-line rulesdir/jsx-click-tracking-ids -- this element doesn't take ID
              onClick={handleLegendClick}
              onMouseEnter={handleLegendMouseEnter}
              onMouseLeave={handleLegendMouseLeave}
              content={
                <ChartLegendContent
                  height={effectiveLegendHeight}
                  topLabel={barsLabel}
                  topKeys={barKeys}
                  bottomGroups={lineGroups}
                  bottomLabel={linesLabel}
                  bottomKeys={lineKeys}
                  layout='vertical'
                  scrollEnabled={scrollEnabled}
                  legendIcons={legendIcons}
                />
              }
            />
          )}
          {barKeys.map((key, idx) => {
            return (
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              <Bar
                yAxisId={1}
                key={key}
                name={keyFormatter == null ? null : keyFormatter(key)}
                isAnimationActive={false}
                dataKey={key}
                // This element doesn't take ID and the library types are wrong
                // eslint-disable-next-line rulesdir/jsx-click-tracking-ids, @typescript-eslint/no-explicit-any
                onClick={onDeepLink ? handleDeepLink : null}
                onMouseEnter={() => setHoveredKey(key)}
                onMouseLeave={() => setHoveredKey(key)}
                // Add in the stack if its stacked
                stackId={stackedBars ? 'barGroups' : null}
                // Use a stacked color when stacking
                fill={
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  stackedBars ? stackColors[idx % stackColors.length] : colors[idx % colors.length]
                }
                fillOpacity={!legendHovered || hoveredKey == null || hoveredKey === key ? 1 : 0.3}
                stroke={legendHovered && hoveredKey === key ? theme.color.border.active : undefined}
                hide={filteredLegendKeys.length > 0 && !filteredLegendKeys.includes(key)}
                style={{
                  transition: `fill-opacity ${TransitionDuration.Fast}, stroke ${TransitionDuration.Fast}`,
                }}
              >
                {renderBarLabel != null && (
                  <LabelList
                    content={(props) => renderBarLabel(props as RenderLabelProps, 0)}
                    position='center'
                  />
                )}
              </Bar>
            );
          })}
          {lineKeys.map((key, idx) => {
            const fillColor = colors[(stackedBars ? idx : idx + barKeys.length) % colors.length];
            return (
              <Line
                yAxisId={2}
                key={key}
                dataKey={key}
                isAnimationActive={false}
                // This element doesn't take ID and the library types are wrong
                onMouseEnter={() => setHoveredKey(key)}
                onMouseLeave={() => setHoveredKey(null)}
                fill={fillColor}
                fillOpacity={!legendHovered || hoveredKey == null || hoveredKey === key ? 1 : 0.3}
                stroke={fillColor}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                strokeWidth={legendHovered && hoveredKey === key ? 2 : null}
                hide={filteredLegendKeys.length > 0 && !filteredLegendKeys.includes(key)}
                style={{
                  transition: `fill-opacity ${TransitionDuration.Fast}, stroke ${TransitionDuration.Fast}`,
                }}
              />
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartLayout>
  );
};

export default memo(BarAndLineChartWithLegendV2);

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const TooltipContent: FC<
  ComponentProps<typeof Tooltip> & {
    active?: boolean;
    label?: string;
    payload?: any[];
    hoveredKey: string | null;
    createHeader: (label: string) => string;
    legendFormatter: (value: string) => string;
    keyToUnit: (value: string) => string;
    showZeros?: boolean;
    legendNumberOpts?: Intl.NumberFormatOptions;
    tooltipRef: RefObject<HTMLDivElement>;
  }
> = ({
  active,
  payload,
  label,
  hoveredKey,
  legendFormatter,
  createHeader,
  keyToUnit,
  showZeros,
  legendNumberOpts,
  tooltipRef,
}) => {
  if (!active || payload == null) {
    return null;
  }
  const countLabels = payload
    .slice()
    .map(
      ({ color, dataKey, value }) =>
        typeof value === 'number' &&
        (value > 0 || (showZeros && value === 0)) && (
          <ChartTooltipLabelContainer
            ref={tooltipRef}
            key={dataKey}
            active={true}
            hasActive={hoveredKey != null}
          >
            <StyledChartTooltipLabel className='title'>
              <Dot style={{ color }} /> {legendFormatter(String(dataKey))}
            </StyledChartTooltipLabel>
            <StyledChartTooltipLabel>
              {formatNumber(+value, legendNumberOpts)}
              {keyToUnit(dataKey as string)}
            </StyledChartTooltipLabel>
          </ChartTooltipLabelContainer>
        ),
    )
    .filter(Boolean);

  return (
    countLabels.length > 0 && (
      <ChartTooltipContainer>
        <ChartTooltipHeader>{createHeader(label)}</ChartTooltipHeader>
        {countLabels}
      </ChartTooltipContainer>
    )
  );
};
